import { useEffect, useRef, useState, ChangeEvent } from "react";
import { Check, ChevronDown } from "@digital-twins/design-system/icons";

import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { MatrixColumnScaleProps, MatrixFieldCommitMode } from "./types";

const ENDPOINT_INPUT_CLASS =
  "h-auto min-w-0 flex-1 border-0 bg-transparent px-0 py-0 text-center text-[10.5px] leading-[1.3] text-muted-foreground shadow-none focus-visible:ring-0";

const MENU_ITEM_CLASS =
  "flex w-full items-center justify-between gap-2 rounded-[6px] px-2.5 py-1.5 text-left text-[12px] text-foreground transition-colors hover:bg-muted";

interface MatrixScaleEndpointProps {
  value: string;
  onChange: (next: string) => void;
  commitMode: MatrixFieldCommitMode;
  disabled?: boolean;
  disabledReason?: string;
  placeholder?: string;
  ariaLabel: string;
  dataTestId: string;
}

function MatrixScaleEndpoint(props: MatrixScaleEndpointProps) {
  const {
    value,
    onChange,
    commitMode,
    disabled,
    disabledReason,
    placeholder,
    ariaLabel,
    dataTestId,
  } = props;

  const [draft, setDraft] = useState(value);
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (!isFocusedRef.current) {
      setDraft(value);
    }
  }, [value]);

  const displayValue = commitMode === "blur" ? draft : value;

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const next = event.target.value;
    if (commitMode === "change") {
      onChange(next);
      return;
    }
    setDraft(next);
  }

  function handleFocus() {
    isFocusedRef.current = true;
  }

  function handleBlur() {
    isFocusedRef.current = false;
    if (commitMode !== "blur") {
      return;
    }
    const next = draft.trim();
    if (next !== value) {
      onChange(next);
    }
  }

  return (
    <Input
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={ENDPOINT_INPUT_CLASS}
      aria-label={ariaLabel}
      disabled={disabled}
      title={disabled ? disabledReason : undefined}
      data-testid={dataTestId}
    />
  );
}

export function MatrixColumnScale(props: MatrixColumnScaleProps) {
  const {
    pickerLabel,
    pickerOptions,
    selectedOptionId,
    onPick,
    minValue,
    maxValue,
    onMinChange,
    onMaxChange,
    commitMode,
    minPlaceholder,
    maxPlaceholder,
    pickerAriaLabel,
    minAriaLabel,
    maxAriaLabel,
    disabled,
    disabledReason,
    testId,
    minTestId,
    maxTestId,
  } = props;

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  function handlePick(id: string) {
    setIsMenuOpen(false);
    onPick(id);
  }

  return (
    <div className="flex flex-col items-center gap-0.5">
      <Popover open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="inline-flex max-w-full items-center gap-1 rounded-[4px] px-1.5 py-0.5 text-[10.5px] font-normal leading-[1.3] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
            aria-label={pickerAriaLabel}
            disabled={disabled}
            title={disabled ? disabledReason : undefined}
            data-testid={testId}
          >
            <span className="truncate">{pickerLabel}</span>
            <ChevronDown className="h-3 w-3 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="center" className="w-44 p-1">
          {pickerOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              className={MENU_ITEM_CLASS}
              onClick={() => handlePick(option.id)}
              disabled={disabled}
              data-testid={`${testId}-option-${option.id}`}
            >
              {option.label}
              {option.id === selectedOptionId ? (
                <Check className="h-3 w-3" />
              ) : null}
            </button>
          ))}
        </PopoverContent>
      </Popover>
      <div className="flex w-full items-center gap-1">
        <MatrixScaleEndpoint
          value={minValue}
          onChange={onMinChange}
          commitMode={commitMode}
          disabled={disabled}
          disabledReason={disabledReason}
          placeholder={minPlaceholder}
          ariaLabel={minAriaLabel}
          dataTestId={minTestId}
        />
        <span
          className={
            disabled
              ? "shrink-0 text-[10.5px] text-muted-foreground/70 opacity-50"
              : "shrink-0 text-[10.5px] text-muted-foreground/70"
          }
        >
          &rarr;
        </span>
        <MatrixScaleEndpoint
          value={maxValue}
          onChange={onMaxChange}
          commitMode={commitMode}
          disabled={disabled}
          disabledReason={disabledReason}
          placeholder={maxPlaceholder}
          ariaLabel={maxAriaLabel}
          dataTestId={maxTestId}
        />
      </div>
    </div>
  );
}
